/**
 * A rework the case agent asks for: a partner's return sent back for
 * correction — what it found, table by table, and the message it drafted,
 * edited here and sent by a person: the return as it stands goes back onto
 * the partner's request, then the message on their thread (each once); or
 * not sent, saying why. Never approved blind — sending is the answer, and
 * the partner's corrected form comes back as a new return.
 */

import { useState } from 'react';

import { Anchor, Button, Group, List, Stack, Text, Textarea, TextInput } from '@mantine/core';
import { notifications } from '@mantine/notifications';

import * as Sentry from '@sentry/react';

import { ApiConflictError, vnextApiClient } from '@/services/vnext-api';
import type { Intervention, ReworkSent } from '@/types/workbench';
import { toError } from '@/utils/errors';
import { AgentText } from '../AgentText';
import { useWorkbenchKeys } from '../useWorkbenchKeys';
import { AskHeading, Kbd, listOf, textOf } from './common';
import classes from '../NeedsYou.module.css';

/** What sending came to, in words. */
export function sentMessage(sent: ReworkSent): { color: 'teal' | 'orange'; message: string } {
  switch (sent.outcome) {
    case 'sent':
      return { color: 'teal', message: 'Sent — the partner has the form back, and your message.' };
    case 'message_uncertain':
      return {
        color: 'orange',
        message: `The form is back on the partner's request; whether the message reached their thread is unknown${
          sent.detail ? ` (${sent.detail})` : ''
        } — check the thread before sending it again.`,
      };
    default:
      return {
        color: 'orange',
        message:
          "The form is back on the partner's request; there is no thread to message them on.",
      };
  }
}

interface ReworkPaneProps {
  reportId: string;
  ask: Intervention;
  subjectName: string | null;
  onAnswered: (ids: string[]) => void | Promise<void>;
  /** Open the return it is about, in the Document view. */
  onOpenDocument?: (reportDataId: string) => void;
  keyboard: boolean;
}

export function ReworkPane({
  reportId,
  ask,
  subjectName,
  onAnswered,
  onOpenDocument,
  keyboard,
}: Readonly<ReworkPaneProps>) {
  const [message, setMessage] = useState(textOf(ask.payload.message) ?? '');
  const [why, setWhy] = useState('');
  const [busy, setBusy] = useState<'send' | 'decline' | null>(null);
  const issues = listOf(ask.payload.issues);
  const [returnId] = listOf(ask.target.report_data_ids);
  const ready = message.trim() !== '' && busy === null;

  const failed = async (err: unknown, what: string) => {
    if (!(err instanceof ApiConflictError)) {
      Sentry.captureException(toError(err));
    }
    notifications.show({ color: 'red', message: toError(err, what).message });
    if (err instanceof ApiConflictError) {
      await onAnswered([]);
    }
  };
  const send = async () => {
    if (!ready) {
      return;
    }
    setBusy('send');
    try {
      const sent = await vnextApiClient.sendRework(reportId, ask.id, message.trim());
      notifications.show(sentMessage(sent));
      await onAnswered([ask.id]);
    } catch (err) {
      await failed(err, "The rework couldn't be sent");
    } finally {
      setBusy(null);
    }
  };
  const decline = async () => {
    if (why.trim() === '' || busy !== null) {
      return;
    }
    setBusy('decline');
    try {
      await vnextApiClient.declineRework(reportId, ask.id, why.trim());
      notifications.show({
        color: 'teal',
        message: 'Not sent — the case agent reads why at its next run.',
      });
      await onAnswered([ask.id]);
    } catch (err) {
      await failed(err, "The answer couldn't be recorded");
    } finally {
      setBusy(null);
    }
  };
  useWorkbenchKeys({ 'Mod+Enter': () => void send() }, keyboard);

  return (
    <>
      <div className={classes.detailBody} data-testid="pane-rework">
        <Stack gap="sm">
          <AskHeading
            section={ask.section}
            subjectName={subjectName}
            question={ask.question}
            why={ask.reason}
            blocking={ask.waitingTaskId !== null}
          />
          {returnId && onOpenDocument && (
            <Anchor
              component="button"
              type="button"
              size="xs"
              onClick={() => onOpenDocument(returnId)}
              style={{ alignSelf: 'flex-start' }}
              data-testid="rework-open-return"
            >
              Open the partner&apos;s return
            </Anchor>
          )}
          {issues.length > 0 && (
            <Stack gap={2} data-testid="rework-issues">
              <Text size="xs" fw={600}>
                To correct
              </Text>
              <List size="xs" spacing={2}>
                {issues.map((issue) => (
                  <List.Item key={issue}>
                    <AgentText text={issue} />
                  </List.Item>
                ))}
              </List>
            </Stack>
          )}
          <Text size="xs" c="dimmed">
            Sending puts the return as it stands back on the partner&apos;s request, then your
            message on their thread; their corrected form comes back as a new return.
          </Text>
        </Stack>
      </div>
      <Stack gap={6} className={classes.answers} data-testid={`rework-bar-${ask.id}`}>
        <Textarea
          size="xs"
          label="Message to the partner"
          autosize
          minRows={4}
          placeholder="What to correct, table by table (⌘↵ sends)"
          value={message}
          onChange={(e) => setMessage(e.currentTarget.value)}
          data-testid="rework-message"
        />
        <Group gap={6} wrap="nowrap">
          <Button
            size="xs"
            onClick={() => void send()}
            disabled={!ready}
            loading={busy === 'send'}
            data-testid="rework-send"
          >
            Send to partner
            <Kbd>⌘↵</Kbd>
          </Button>
        </Group>
        <Group gap={6} wrap="nowrap">
          <TextInput
            size="xs"
            placeholder="Not sending? Say why"
            aria-label="Why it isn't sent"
            value={why}
            onChange={(e) => setWhy(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.metaKey && !e.ctrlKey) {
                e.preventDefault();
                void decline();
              }
            }}
            style={{ flex: 1 }}
            data-testid="rework-why"
          />
          <Button
            size="xs"
            variant="default"
            onClick={() => void decline()}
            disabled={why.trim() === '' || busy !== null}
            loading={busy === 'decline'}
            data-testid="rework-decline"
          >
            Don&apos;t send
          </Button>
        </Group>
      </Stack>
    </>
  );
}
